import { queryOptions } from '@tanstack/react-query';
import { ApiError } from '@discovr/common';
import type {
  AudienceReport,
  CreatorContentReport,
  GeneralReport,
  ReportPeriod,
  SentimentReport,
  SentimentReportQuery,
} from '@discovr/types';
import { campaignKeys, SENTIMENT_CREATOR_PAGE_SIZE, type CreatorContentParams } from '../queryKeys';
import { getData } from './shared';

export { SENTIMENT_CREATOR_PAGE_SIZE };

const REPORT_STALE_MS = 30_000;
// The server gives a report 25 s to build; wait past it so the client never gives up first.
const REPORT_REQUEST_TIMEOUT_MS = 40_000;
const HTTP_SERVER_ERROR_MIN = 500;
/** The status the shared client reports when no response arrived (offline, timeout). */
const NO_RESPONSE_STATUS = 0;
const MAX_REPORT_RETRIES = 1;

/** A report is heavy to build: retry once for a server fault or a lost response, never for a 4xx (429 included). */
export function shouldRetryReport(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_REPORT_RETRIES) return false;
  if (!(error instanceof ApiError)) return false;
  return error.httpStatus === NO_RESPONSE_STATUS || error.httpStatus >= HTTP_SERVER_ERROR_MIN;
}

function reportUrl(campaignId: string, tab: string): string {
  return `/campaigns/${encodeURIComponent(campaignId)}/report/${tab}`;
}

function reportRequest(signal: AbortSignal, period?: ReportPeriod) {
  return { timeout: REPORT_REQUEST_TIMEOUT_MS, signal, ...(period ? { params: { period } } : {}) };
}

const REPORT_QUERY_DEFAULTS = { staleTime: REPORT_STALE_MS, retry: shouldRetryReport } as const;

export const generalReportOptions = (campaignId: string, period?: ReportPeriod) =>
  queryOptions({
    queryKey: campaignKeys.generalReport(campaignId, period),
    queryFn: ({ signal }) => getData<GeneralReport>(reportUrl(campaignId, 'general'), reportRequest(signal, period)),
    ...REPORT_QUERY_DEFAULTS,
  });

function sentimentParams(q: SentimentReportQuery) {
  return {
    ...(q.from ? { from: q.from } : {}),
    ...(q.to ? { to: q.to } : {}),
    ...(q.creatorLimit ? { creator_limit: q.creatorLimit } : {}),
    ...(q.creatorOffset ? { creator_offset: q.creatorOffset } : {}),
  };
}

export const sentimentReportOptions = (campaignId: string, query: SentimentReportQuery) =>
  queryOptions({
    queryKey: campaignKeys.sentimentReport(campaignId, query),
    queryFn: ({ signal }) =>
      getData<SentimentReport>(reportUrl(campaignId, 'sentiment'), {
        timeout: REPORT_REQUEST_TIMEOUT_MS,
        signal,
        params: sentimentParams(query),
      }),
    ...REPORT_QUERY_DEFAULTS,
  });

export const audienceReportOptions = (campaignId: string) =>
  queryOptions({
    queryKey: campaignKeys.audienceReport(campaignId),
    queryFn: ({ signal }) => getData<AudienceReport>(reportUrl(campaignId, 'audience'), reportRequest(signal)),
    ...REPORT_QUERY_DEFAULTS,
  });

export const creatorContentReportOptions = (campaignId: string, params: CreatorContentParams) =>
  queryOptions({
    queryKey: campaignKeys.creatorContentReport(campaignId, params),
    queryFn: ({ signal }) =>
      getData<CreatorContentReport>(reportUrl(campaignId, 'creator-content'), {
        timeout: REPORT_REQUEST_TIMEOUT_MS,
        signal,
        params,
      }),
    ...REPORT_QUERY_DEFAULTS,
  });
